import * as React from 'react';
import classNames from 'styles/utils/classNames';
import type { MarketSessionPhase } from 'utils/shared/time/marketSession';
import StatusDot from './StatusDot';

interface Props {
  /** The session whose colour lights the dot, from `todaysOpenInterestPhase`; null draws none. */
  phase: MarketSessionPhase | null;
  children: React.ReactNode;
  /**
   * Hover text while the dot shows, also read by screen readers. The GEX page passes none: its OI
   * count is a help button, and that button keeps its own card.
   */
  title?: string;
}

/**
 * Today's open interest, marked where the number is (tradedata#74 D7).
 *
 * The panel-row dot, static: no ping, because the ping means a running tape and open interest does
 * not stream. It sits where `LivePriceValue` puts the spot dot, off the top right of the number, and
 * is absolutely positioned, so the number does not move when it appears or goes. The wrapper renders
 * in both states for the same reason.
 */
export default function OpenInterestDot({ phase, children, title }: Props) {
  const label = phase ? title : undefined;
  return (
    <span
      data-oi-today={phase ? 'true' : 'false'}
      className="relative inline-flex items-baseline whitespace-nowrap"
      title={label}
    >
      {children}
      {phase && (
        // With hover text, the dot takes the pointer so hovering it shows the text. Without, it
        // lets the pointer through to whatever sits under it.
        <span
          className={classNames(
            'absolute -right-[7px] -top-px inline-flex',
            !label && 'pointer-events-none',
          )}
        >
          <StatusDot phase={phase} live={false} size="sm" />
        </span>
      )}
      {label && <span className="sr-only">{`, ${label}`}</span>}
    </span>
  );
}
